import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { connect } from 'react-redux';
import FlexiSearchBarWrapper from './FlexiSearchBar/FlexiSearchBarWrapper';
import SearchBar from './SearchBar';
import { destinationSelection, getDestinations, getDestinationType } from 'Actions/destinations';
import * as Hotels from 'Actions/hotels';
import * as Search from 'Actions/search';
import * as Settings from 'Actions/settings';
import * as Translations from 'Actions/translations';
import { getWidgetSet, setConfig } from 'Actions/widgetPool';
import { Redirect, WidgetConfigHelper } from 'Helpers';
import HotelsProvider from 'State/HotelsContext';
import WidgetContext from 'State/WidgetContext';


const SearchBarContainer = (props) => {
    const {
        search, translations, container, dispatchGetDestinations,
        id, channelId, onWidgetLoaded, configuration, settings,
    } = props;

    const { destinationsFilterEnabled } = configuration;

    async function fetchDestinations() {
        if (destinationsFilterEnabled) {
            const { language } = search;
            const user = { language };
            const params = { channelId, user };
            await dispatchGetDestinations(params);
        }
    }

    useEffect(() => {
        if (onWidgetLoaded) {
            onWidgetLoaded();
        }
    }, []);

    useEffect(() => {
        fetchDestinations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ destinationsFilterEnabled ]);

    // FIXME: extract this or use useIntl when react-intl is updated
    const intlProvider = new IntlProvider({ locale: search.language, messages: translations });
    const { intl } = intlProvider.getChildContext();
    const promoCodePlaceholder = intl.formatMessage({ id: 'bf.havePromocode', defaultMessage: 'bf.havePromocode' });
    const searchInputPlaceHolder = intl.formatMessage({ id: 'bf.locationOrHotelNameV2', defaultMessage: 'bf.locationOrHotelNameV2' });

    return (
        <WidgetContext.Provider value={{ id }}>
            <HotelsProvider id={id} widgetType="searchbar">
                <IntlProvider locale={search.language} messages={translations}>
                    {settings.searchBarWidget === 'LEAN' ? (
                        <div>
                            <FlexiSearchBarWrapper
                                {...props}
                            />
                        </div>
                    ) : (
                        <SearchBar
                            {...props}
                            container={container}
                            searchInputPlaceHolder={searchInputPlaceHolder}
                            promoCodePlaceholder={promoCodePlaceholder}
                        />
                    )}
                </IntlProvider>
            </HotelsProvider>

        </WidgetContext.Provider>
    );
};

SearchBarContainer.propTypes = {
    id: PropTypes.string.isRequired,
    search: PropTypes.object.isRequired,
    translations: PropTypes.object.isRequired,
    container: PropTypes.object,
    dispatchGetDestinations: PropTypes.func.isRequired,
    onWidgetLoaded: PropTypes.func,
    configuration: PropTypes.object.isRequired,
    channelId: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => {
    const { id } = ownProps;
    const configuration = state.widgetPool.find(widgetSet => widgetSet.config.id === id).searchbar;
    return ({
        settings: state.settings.data,
        search: state.search.data,
        widgetPool: state.widgetPool,
        translations: state.translations.data,
        isLoading: state.search.isLoading,
        destinationsLoading: state.destinations.isLoading || false,
        error: state.search.error,
        channelId: state.settings && state.settings.data.channelId,
        regionOptions: state.destinations.data,
        destinationsFilterEnabled: configuration.destinationsFilterEnabled,
        isMultiProperty: state.settings.data.isMultiProperty,
        destinationSelected: state.destinations && state.destinations.selected,
        configuration,
        hotelsFilters: state.settings.data?.hotelsFilters,
        filters: state.hotels.filters,
        autocompleteFilterApplied: state.hotels.autocompleteFilter,
        coupon: state.hotels.coupon || state.search.data?.coupon,
        promoCode: state.search.data?.clientPromoCode,
        isDatePickerWrapperEnabled: state.url['date-picker-wrapper-enabled'] === 'true',
    });
};

const mapDispatchToProps = dispatch => ({
    getCurrentWidgetSet: id => (dispatch(getWidgetSet(id))),
    // TODO: avoid this receiving too many params, should be refactored
    searchResults: async (id, isConnected, configuration, destinationSelected, urlParams, isMultiProperty, filters) => {
        dispatch(Search.searchResults(id));
        const { channelId, hotels } = configuration;
        const searchFilters = destinationSelected ? WidgetConfigHelper.buildFiltersObject(destinationSelected)
            : filters;
        const params = { channelId, hotels, searchFilters };
        if (isConnected) {
            if (!isMultiProperty) {
                await dispatch(Settings.reloadRatesAndOffers(id));
                await dispatch(Settings.updateFuturePackages(id));
                await dispatch(Settings.getStyles());
            } else {
                dispatch(Hotels.getHotelsList(params));
            }
        } else {
            Redirect(urlParams);
        }
    },
    onChangePromoCode: async (id, promoCode) => {
        dispatch(Search.changePromoCode(id, promoCode));
    },
    onChangeCurrency: (id, currency, isMultiProperty) => {
        dispatch(Search.changeCurrency(currency));
        if (!isMultiProperty) {
            dispatch(Settings.reloadRatesAndOffers(id));
        }
    },
    onChangeLanguage: (id, lang, isMultiProperty, channelId, propertyCode) => {
        dispatch(Search.changeLanguage(lang));
        dispatch(Translations.getTranslations(lang, channelId, propertyCode));
        dispatch(Settings.replaceVoucherTerm(lang));
        if (!isMultiProperty) {
            dispatch(Settings.reloadRatesAndOffers(id));
        }
    },
    onChangeRoomsQuantity: (id, numberOfRooms) => dispatch(Search.changeRoomsQuantity(id, numberOfRooms)),
    onRemoveRoom: (id, index) => dispatch(Search.removeRoom(id, index)),
    onChangeAdults: (id, roomIndex, numberOfAdults) => dispatch(Search.changeAdults(id, roomIndex, numberOfAdults)),
    onChangeChildren: (id, roomIndex, numberOfChildren) => dispatch(Search.changeChildren(id, roomIndex, numberOfChildren)),
    onChangeChildrenAge: (id, roomIndex, childIndex, childrenAge) =>
        dispatch(Search.changeChildrenAge(id, roomIndex, childIndex, childrenAge)),
    onChangeDates: (id, dates) => {
        const { startDate, endDate } = dates;
        const checkIn = startDate ? startDate.format('YYYY-MM-DD') : null;
        const checkOut = endDate ? endDate.format('YYYY-MM-DD') : null;

        dispatch(Search.changeDates(id, checkIn, checkOut));
    },
    dispatchGetDestinations: async (params) => {
        const body = { ...params };
        await dispatch(getDestinations(body));
    },
    dispatchReloadRatesAndOffers: id => dispatch(Settings.reloadRatesAndOffers(id)),
    onDestinationSelected: async (selected, id, regionOptions) => {
        const destinationType = await dispatch(getDestinationType(selected));
        await dispatch(setConfig(id, 'searchbar', { searchButtonEnabled: true }));
        let countryEnglishName;
        if (destinationType === 'city') {
            countryEnglishName = WidgetConfigHelper.getCountryEnglishName(regionOptions, selected.code);
        }
        const destination = !countryEnglishName ? { ...selected, type: destinationType }
            : { ...selected, type: destinationType, countryEnglishName };
        await dispatch(destinationSelection(destination));
    },
    getUrlParams: (id, readable) => dispatch(Search.getURLParams(id, null, null, readable)),

    dispatchApplyCoupon: code => dispatch(Search.updateCoupon(code)),

});

export default connect(mapStateToProps, mapDispatchToProps)(SearchBarContainer);

// i18nLabel[bf.havePromocode]
